import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Icon,
  Badge,
  ButtonGroup,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  SimpleGrid,
  Skeleton,
  Switch,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LuChevronDown, LuFilter, LuTriangleAlert } from 'react-icons/lu'
import axiosClient from '../../api/axios'
import { siteLabel } from './ChuActivity'

/**
 * La comptabilité du flux de bandelettes, et les incidents de lecture.
 *
 * POURQUOI CE PANNEAU EXISTE. Un taux d'accord sans dénominateur ne vaut rien :
 * on ignore sur quelle fraction de la série il porte. STARD réclame un
 * diagramme de flux, GRRAS réclame le sort des observations écartées, et les
 * deux pour la même raison — une exclusion silencieuse des cas difficiles se
 * lit comme une bonne performance. Ce panneau rend ce décompte.
 *
 * POURQUOI CÔTÉ ADMIN. Le CHU n'a besoin que du dénominateur : combien de ses
 * bandelettes sont entrées dans la comparaison. Le détail — combien
 * d'identifiants son équipe a corrigés, combien de lignes de contrôle elle a
 * repositionnées — mesure IRIS, pas le laboratoire. Il nous regarde.
 *
 * CE QUE MESURENT CES CHIFFRES. Aucun n'a été instrumenté pour l'occasion : ce
 * sont les traces que l'opérateur laisse en travaillant. Un identifiant corrigé
 * à la main dit que la lecture automatique était fausse ; une ligne de contrôle
 * repositionnée, qu'elle était introuvable. D'où leur valeur et leur limite —
 * ils comptent les erreurs QUE L'OPÉRATEUR A VUES.
 */

type Period = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months' | 'this_year' | 'all'

const PERIOD_LABELS: Record<Period, string> = {
  this_month: 'Ce mois-ci',
  last_3_months: '3 derniers mois',
  last_6_months: '6 derniers mois',
  last_12_months: '12 derniers mois',
  this_year: 'Cette année',
  all: 'Depuis le début',
}

interface FlowRow {
  site: string
  detected: number
  controls: number
  discarded: number
  samples: number
  concluded: number
  underVeil: number
  compared: number
  idUnread: number
  idConflict: number
  idEdited: number
  controlLineFixed: number
  bandsFixed: number
  moved: number
}

interface FlowResponse {
  period: string
  includeInternal: boolean
  sites: FlowRow[]
  totals: FlowRow
  byMonth: Array<{ month: string; samples: number; idUnread: number }>
}

const pct = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 100)} %` : '—')

const formatMonth = (m: string) => {
  const [y, mo] = m.split('-')
  return `${['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'][Number(mo) - 1]} ${y.slice(2)}`
}

/** Une étape du flux : son effectif, et ce qu'il représente de l'entrée. */
function Stage({
  label,
  value,
  of,
  tone,
  note,
}: {
  label: string
  value: number
  of: number
  tone: string
  note?: string
}) {
  return (
    <Box>
      <HStack justify="space-between" align="baseline" mb={1}>
        <Text fontSize="xs" color="gray.600" fontWeight="600">{label}</Text>
        <HStack spacing={1.5} align="baseline">
          <Text fontSize="lg" fontWeight="700" lineHeight="1.1" color={`${tone}.600`}>{value}</Text>
          <Text fontSize="2xs" color="gray.500">{pct(value, of)}</Text>
        </HStack>
      </HStack>
      {/* La barre porte la proportion, le chiffre porte l'effectif : sur un
          flux, c'est la chute d'une étape à l'autre qui se lit, pas la
          valeur absolue. */}
      <Progress
        value={of > 0 ? (value / of) * 100 : 0}
        size="sm"
        colorScheme={tone}
        borderRadius="full"
        bg="gray.100"
      />
      {note && <Text fontSize="2xs" color="gray.500" mt={1}>{note}</Text>}
    </Box>
  )
}

export default function BlotFlowAudit() {
  const [period, setPeriod] = useState<Period>('all')
  const [includeInternal, setIncludeInternal] = useState(true)
  const [site, setSite] = useState<string>('')

  const { data, isLoading, error } = useQuery<FlowResponse>({
    queryKey: ['blotFlow', period, includeInternal],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/lab-analysis/blot-flow?period=${period}&includeInternal=${includeInternal}`,
      )
      return response.data
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const sites = data?.sites ?? []
  const shown: FlowRow | null = useMemo(() => {
    if (!data) return null
    if (!site) return data.totals
    return sites.find((s) => s.site === site) ?? data.totals
  }, [data, site, sites])

  // Le mois où la lecture d'identifiant est entrée en service se lit dans la
  // série : avant lui, « identifiant non lu » signifie « pas encore lisible »,
  // après, un échec réel. Sans cette distinction, le bloc historique passe
  // pour un taux d'échec actuel.
  const worstMonth = useMemo(() => {
    const months = data?.byMonth ?? []
    return months.reduce<{ month: string; rate: number } | null>((worst, m) => {
      if (m.samples < 20) return worst
      const rate = m.idUnread / m.samples
      return !worst || rate > worst.rate ? { month: m.month, rate } : worst
    }, null)
  }, [data?.byMonth])

  const INCIDENTS: Array<{ key: keyof FlowRow; label: string; info: string }> = [
    {
      key: 'idUnread',
      label: 'Identifiant non lu',
      info: "Aucun identifiant n'a été lu sur la bandelette. Sur les plaques antérieures à la mise en service de la lecture d'identifiant, c'est normal et non un échec — voir la série mensuelle.",
    },
    {
      key: 'idConflict',
      label: 'Identifiant en conflit',
      info: 'Deux bandelettes de la même plaque ont reçu le même identifiant. Le logiciel le signale et laisse trancher.',
    },
    {
      key: 'idEdited',
      label: 'Identifiant corrigé',
      info: "L'opérateur a réécrit l'identifiant : la lecture automatique était fausse et il l'a vue. C'est la mesure la plus directe de la qualité de l'OCR.",
    },
    {
      key: 'controlLineFixed',
      label: 'Ligne de contrôle repositionnée',
      info: "L'opérateur a déplacé la ligne de contrôle : elle était introuvable ou mal placée. Toute l'échelle des masses en dépend, donc une erreur ici déplace toutes les bandes.",
    },
    {
      key: 'bandsFixed',
      label: 'Bandes retouchées',
      info: "Au moins une bande a été ajoutée ou supprimée à la main sur cette bandelette.",
    },
    {
      key: 'moved',
      label: 'Bandelette repositionnée',
      info: 'La découpe de la bandelette a été corrigée à la main : la détection avait mal cadré.',
    },
  ]

  if (error) {
    return (
      <Box borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="xl" p={4}>
        <Text fontSize="sm" color="red.700">
          Comptabilité du flux indisponible : {(error as Error).message}
        </Text>
      </Box>
    )
  }

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="2xl" bg="white" p={{ base: 4, md: 5 }}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3} mb={4}>
        <Box>
          <HStack spacing={2} mb={1}>
            <Icon as={LuFilter} color="purple.500" />
            <Heading size="sm">Comptabilité du flux</Heading>
          </HStack>
          <Text fontSize="xs" color="gray.600" maxW="640px">
            Le dénominateur de toute mesure d'accord : combien de bandelettes sont entrées,
            combien en sont sorties et pourquoi. Tout se calcule sur les traces que l'opérateur
            laisse en travaillant, donc aussi sur les cas déjà traités.
          </Text>
        </Box>
        <HStack spacing={3} align="center">
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.600">Nos propres plaques</Text>
            <Switch
              size="sm"
              colorScheme="purple"
              isChecked={includeInternal}
              onChange={(e) => setIncludeInternal(e.target.checked)}
            />
          </HStack>
          <Menu>
            <MenuButton as={Button} size="xs" variant="outline" rightIcon={<LuChevronDown />}>
              {PERIOD_LABELS[period]}
            </MenuButton>
            <MenuList>
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <MenuItem key={p} fontSize="sm" onClick={() => setPeriod(p)}>
                  {PERIOD_LABELS[p]}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {isLoading || !shown ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {[0, 1, 2].map((i) => <Skeleton key={i} height="90px" borderRadius="lg" />)}
        </SimpleGrid>
      ) : (
        <>
          <ButtonGroup size="xs" isAttached variant="outline" mb={4} flexWrap="wrap">
            <Button
              onClick={() => setSite('')}
              bg={site === '' ? 'purple.50' : undefined}
              borderColor={site === '' ? 'purple.300' : undefined}
              color={site === '' ? 'purple.700' : undefined}
            >
              Tous les sites
            </Button>
            {sites.map((s) => (
              <Button
                key={s.site}
                onClick={() => setSite(s.site)}
                bg={site === s.site ? 'purple.50' : undefined}
                borderColor={site === s.site ? 'purple.300' : undefined}
                color={site === s.site ? 'purple.700' : undefined}
              >
                {siteLabel(s.site)}
              </Button>
            ))}
          </ButtonGroup>

          {/* Deux voies, pas un entonnoir unique : une bandelette sous voile
              n'est pas forcément conclue, et une bandelette conclue en
              prospectif n'entre jamais dans une comparaison. Les empiler
              ferait une cascade fausse. */}
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5} mb={5}>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Ce qui entre
              </Text>
              <Stage label="Bandelettes détectées" value={shown.detected} of={shown.detected} tone="purple" />
              <Stage
                label="Échantillons"
                value={shown.samples}
                of={shown.detected}
                tone="purple"
                note={`moins ${shown.controls} témoin${shown.controls > 1 ? 's' : ''} désigné${shown.controls > 1 ? 's' : ''} et ${shown.discarded} faux positif${shown.discarded > 1 ? 's' : ''} masqué${shown.discarded > 1 ? 's' : ''}`}
              />
            </VStack>

            <VStack align="stretch" spacing={3}>
              <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Voie prospective
              </Text>
              <Stage
                label="Conclues par l'opérateur"
                value={shown.concluded}
                of={shown.samples}
                tone="teal"
                note={`${shown.samples - shown.concluded} bandelette${shown.samples - shown.concluded > 1 ? 's' : ''} sans conclusion enregistrée`}
              />
            </VStack>

            <VStack align="stretch" spacing={3}>
              <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Voie rétrospective
              </Text>
              <Stage label="Lues sous voile" value={shown.underVeil} of={shown.samples} tone="orange" />
              <Stage
                label="Entrées dans la comparaison"
                value={shown.compared}
                of={shown.underVeil}
                tone="orange"
                note={
                  shown.underVeil > shown.compared
                    ? `${shown.underVeil - shown.compared} bandelette${shown.underVeil - shown.compared > 1 ? 's' : ''} sous voile sans paire : saisie de paillasse incomplète ou instantané IRIS manquant`
                    : undefined
                }
              />
            </VStack>
          </SimpleGrid>

          {/* La fuite qui compte : des bandelettes voilées dont la paire ne
              s'est jamais formée. Elles sont dans le dénominateur de l'étude
              et absentes de son numérateur — exactement ce qu'un relecteur
              cherche. */}
          {shown.underVeil > shown.compared && (
            <HStack
              align="flex-start"
              spacing={2}
              borderWidth="1px"
              borderColor="orange.200"
              bg="orange.50"
              borderRadius="lg"
              p={3}
              mb={5}
            >
              <Icon as={LuTriangleAlert} color="orange.500" mt={0.5} />
              <Text fontSize="xs" color="orange.800">
                {shown.underVeil - shown.compared} bandelette{shown.underVeil - shown.compared > 1 ? 's' : ''} sur{' '}
                {shown.underVeil} ont été lues sous voile sans entrer dans la comparaison
                ({pct(shown.underVeil - shown.compared, shown.underVeil)} de perte). À expliquer
                dans le dossier : une série dont on ignore ce qu'elle a perdu ne se défend pas.
              </Text>
            </HStack>
          )}

          <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
            Incidents de lecture, par site
          </Text>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Incident</Th>
                  {sites.map((s) => <Th key={s.site} isNumeric>{siteLabel(s.site)}</Th>)}
                  <Th isNumeric>Ensemble</Th>
                </Tr>
              </Thead>
              <Tbody>
                {INCIDENTS.map((inc) => (
                  <Tr key={inc.key as string}>
                    <Td>
                      <Tooltip label={inc.info} hasArrow maxW="340px" placement="right">
                        <Text fontSize="xs" cursor="help" borderBottomWidth="1px" borderStyle="dotted" display="inline">
                          {inc.label}
                        </Text>
                      </Tooltip>
                    </Td>
                    {sites.map((s) => (
                      <Td key={s.site} isNumeric fontSize="xs">
                        {(s[inc.key] as number) || 0}
                        <Text as="span" color="gray.400" ml={1.5}>{pct(s[inc.key] as number, s.samples)}</Text>
                      </Td>
                    ))}
                    <Td isNumeric fontSize="xs" fontWeight="600">
                      {(data!.totals[inc.key] as number) || 0}
                      <Text as="span" color="gray.400" ml={1.5} fontWeight="400">
                        {pct(data!.totals[inc.key] as number, data!.totals.samples)}
                      </Text>
                    </Td>
                  </Tr>
                ))}
                <Tr>
                  <Td><Text fontSize="xs" color="gray.500">Échantillons (dénominateur)</Text></Td>
                  {sites.map((s) => <Td key={s.site} isNumeric fontSize="xs" color="gray.500">{s.samples}</Td>)}
                  <Td isNumeric fontSize="xs" color="gray.500" fontWeight="600">{data!.totals.samples}</Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>

          {/* La série mensuelle ne sert qu'à une chose, mais elle est
              nécessaire : elle montre que le gros des identifiants non lus est
              historique. Une seule série, donc aucune légende — le titre la
              nomme. */}
          {(data?.byMonth?.length ?? 0) > 1 && (
            <Box mt={5}>
              <HStack justify="space-between" align="baseline" mb={2}>
                <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Part d'identifiants non lus, par mois
                </Text>
                {worstMonth && (
                  <Badge colorScheme="gray" variant="subtle" fontSize="2xs">
                    pic en {formatMonth(worstMonth.month)} · {Math.round(worstMonth.rate * 100)} %
                  </Badge>
                )}
              </HStack>
              <SimpleGrid columns={{ base: 3, md: 6 }} spacing={3}>
                {data!.byMonth.map((m) => (
                  <Box key={m.month}>
                    <Text fontSize="2xs" color="gray.500">{formatMonth(m.month)}</Text>
                    <Text fontSize="md" fontWeight="700" lineHeight="1.2" color="gray.700">
                      {pct(m.idUnread, m.samples)}
                    </Text>
                    <Progress
                      value={m.samples > 0 ? (m.idUnread / m.samples) * 100 : 0}
                      size="xs"
                      colorScheme="purple"
                      borderRadius="full"
                      bg="gray.100"
                      mt={1}
                    />
                    <Text fontSize="2xs" color="gray.400">{m.idUnread} / {m.samples}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <Text fontSize="2xs" color="gray.400" mt={4}>
            Ces compteurs mesurent les erreurs que l'opérateur a vues et corrigées. Une erreur
            qu'il n'a pas vue n'y figure pas : c'est le rôle de la comparaison rétrospective, pas
            de ce tableau.
          </Text>
        </>
      )}
    </Box>
  )
}
